import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import * as Sentry from "@sentry/react-native";
import { uploadAvatar, deleteAvatar, isLocalFileUri } from "./storage";
import type { FollowProfile } from "./followers";
import type { ProfileUpdateFormData } from "@/features/profile/validation/schema";
import type { Profile } from "@lezz/shared";
import { useAuthContext } from "@/features/auth/hooks/use-auth-context";
import { apiFetch, ApiError } from "@/shared/lib/bff/client";
import { logger } from "@/shared/lib/logger";
import { authTrace } from "@/features/auth/lib/auth-trace";

export const profileKeys = {
  all: ["profile"] as const,
  byUserId: (userId: string) => [...profileKeys.all, userId] as const,
  // No collision with byUserId: userIds are UUIDs, never "search"
  search: (query: string) => [...profileKeys.all, "search", query] as const,
};

export const usernameCheckKeys = {
  byUsername: (username: string) => ["username-check", username] as const,
};

export async function checkUsernameAvailability(
  username: string
): Promise<{ available: boolean }> {
  return apiFetch<{ available: boolean }>(
    `/api/profile/username-check?username=${encodeURIComponent(username)}`
  );
}

export function useUsernameAvailability(username: string, enabled: boolean) {
  return useQuery({
    queryKey: usernameCheckKeys.byUsername(username),
    queryFn: () => checkUsernameAvailability(username),
    enabled: enabled && !!username,
    staleTime: 30_000,
  });
}

export async function searchProfiles(query: string): Promise<FollowProfile[]> {
  return apiFetch<FollowProfile[]>(
    `/api/profile/search?q=${encodeURIComponent(query)}`
  );
}

export function useSearchProfiles(query: string) {
  const normalizedQuery = query.trim();
  return useQuery({
    queryKey: profileKeys.search(normalizedQuery),
    queryFn: () => searchProfiles(normalizedQuery),
    enabled: normalizedQuery.length > 0,
  });
}

export async function fetchProfileByUserId(
  userId?: string
): Promise<Profile | null> {
  if (!userId) {
    throw new Error("Not authenticated");
  }

  const span = authTrace.profileStart(userId);
  try {
    const profile = await apiFetch<Profile>(`/api/profile/${userId}`);
    authTrace.profileResolved(span, { outcome: "success" });

    return profile;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      authTrace.profileResolved(span, { outcome: "not-found" });
      return null;
    }
    authTrace.profileError(span, e, 1);
    throw e;
  }
}

export function useProfile(userId?: string) {
  return useQuery({
    queryKey: profileKeys.byUserId(userId!),
    queryFn: async () => fetchProfileByUserId(userId),
    enabled: Boolean(userId),
    staleTime: Infinity,
  });
}

// Suspense variant used in AuthenticatedNavigator to gate navigation.
// 404 → null (→ complete-profile), network/5xx → ErrorBoundary.
export function useProfileSuspense(userId: string) {
  return useSuspenseQuery({
    queryKey: profileKeys.byUserId(userId),
    queryFn: async () => fetchProfileByUserId(userId),
    staleTime: Infinity,
  });
}

export interface UpdateProfileParams {
  userId: string;
  data: ProfileUpdateFormData;
}

/**
 * Handles avatar update logic: upload new, delete old, or remove existing
 * Uploads new avatar first to prevent data loss if upload fails
 */
async function handleAvatarUpdate(
  userId: string,
  currentAvatarUrl: string | null,
  newAvatarUrl: string | null | undefined
): Promise<string | null> {
  const hasExistingAvatar = !!currentAvatarUrl;
  const hasNewLocalFile = !!newAvatarUrl && isLocalFileUri(newAvatarUrl);
  const isRemovingAvatar = hasExistingAvatar && newAvatarUrl === null;

  // Case 1: User wants to remove their avatar
  if (isRemovingAvatar) {
    await deleteAvatar(currentAvatarUrl);
    return null;
  }

  // Case 2: User is uploading a new avatar (new or replacement)
  if (hasNewLocalFile) {
    const uploadedUrl = await uploadAvatar(userId, newAvatarUrl);
    // Delete old avatar only after successful upload
    if (hasExistingAvatar) {
      await deleteAvatar(currentAvatarUrl);
    }
    return uploadedUrl;
  }

  // Case 3: No change to avatar
  return currentAvatarUrl;
}

export async function updateProfile({
  userId,
  data,
}: UpdateProfileParams): Promise<Profile> {
  return Sentry.startSpan(
    { name: "update-profile", op: "user.action" },
    async (span) => {
      span.setAttribute("userId", userId);
      span.setAttribute("hasNewAvatar", !!data.avatarUrl);

      let currentProfile: Profile | null = null;
      try {
        currentProfile = await apiFetch<Profile>(`/api/profile/${userId}`);
      } catch {
        // Profile doesn't exist yet (complete-profile flow)
      }

      const isNewProfile = !currentProfile;
      span.setAttribute("isNewProfile", isNewProfile);

      const avatarUrl = await handleAvatarUpdate(
        userId,
        currentProfile?.avatarUrl ?? null,
        data.avatarUrl
      );

      const profile = await apiFetch<Profile>("/api/profile", {
        method: "PUT",
        body: JSON.stringify({
          username: data.username,
          displayName: data.displayName,
          city: data.city,
          state: data.state,
          country: data.country,
          instagramUsername: data.instagramUsername ?? null,
          shouldShowInstagram: data.shouldShowInstagram ?? false,
          avatarUrl,
        }),
      });

      if (isNewProfile) {
        logger.info("Profile completed", {
          userId,
          hasAvatar: !!avatarUrl,
        });
      }

      return profile;
    }
  );
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProfile,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: profileKeys.byUserId(variables.userId),
      });
    },
  });
}

export function useDeleteAccount() {
  const { session } = useAuthContext();

  return useMutation({
    mutationFn: async () => {
      await apiFetch("/api/account", {
        method: "DELETE",
      });

      logger.info("Account deleted", {
        userId: session?.user.id,
      });
    },
  });
}
